import { ChevronDown, ChevronUp, Recycle } from "lucide-react";
import { formatWeightWithUnit } from "../../../utils/utils";
import { Badge } from "../../ui/common/badge";
import { CardHeader } from "../../ui/common/card";

interface WasteAccordionHeaderProps {
  recordCount: number;
  targetKg: number;
  totalDistribution: number;
  sideStreamUnit: "kg" | "ton";
  hasDistributionMismatch: boolean;
  isErrorMessageVisible: boolean;
  accordionEnabled: boolean;
  onClick: () => void;
}

/**
 * Renders the clickable accordion header for the Waste & Side Stream section.
 * Displays the record count badge, target value, running total, and an inline
 * error message when the total does not match the target.
 */
export const WasteAccordionHeader = ({
  recordCount,
  targetKg,
  totalDistribution,
  sideStreamUnit,
  hasDistributionMismatch,
  isErrorMessageVisible,
  accordionEnabled,
  onClick
}: WasteAccordionHeaderProps) => {
  return (
    <CardHeader
      className="tw-bg-secondary/50 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
      onClick={onClick}
    >
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-items-center tw-justify-between">
          {/* Left side: icon, title, record count badge */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <Recycle className="tw-h-4 tw-w-4 tw-text-primary" />
            <span className="tw-text-sm tw-font-semibold tw-text-foreground">Waste & Side Stream</span>
            {recordCount > 0 && (
              <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                {recordCount}
              </Badge>
            )}
          </div>

          {/* Right side: target value, running total, chevron */}
          <div className="tw-flex tw-items-center tw-gap-2">
            {targetKg > 0 && (
              <span className="tw-text-[10px] tw-font-mono tw-text-muted-foreground">
                Target:{" "}
                <span className="tw-text-foreground">
                  {formatWeightWithUnit(targetKg, sideStreamUnit)} {sideStreamUnit}
                </span>
              </span>
            )}
            <span
              className={`tw-text-xs tw-font-mono ${
                hasDistributionMismatch ? "tw-text-destructive" : "tw-text-muted-foreground"
              }`}
            >
              Total: {formatWeightWithUnit(totalDistribution, sideStreamUnit)} {sideStreamUnit}
            </span>
            {accordionEnabled ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Inline mismatch warning — only visible when accordion is open and totals diverge */}
        {isErrorMessageVisible && (
          <p className="tw-text-[10px] tw-text-destructive tw-font-mono">
            Total ({formatWeightWithUnit(totalDistribution, sideStreamUnit)} {sideStreamUnit}){" "}
            {totalDistribution > targetKg ? "exceeds" : "is less than"} the Waste / Side Stream
            amount specified in the process ({formatWeightWithUnit(targetKg, sideStreamUnit)}{" "}
            {sideStreamUnit}).
          </p>
        )}
      </div>
    </CardHeader>
  );
};
